import React from "react";
import Navbar from "../Navbar/Navbar";
import Footer from "../Footer/Footer";

const SECTIONS = [
  {
    title: "Our Policy — No Refunds",
    red: true,
    content: "All purchases made on PilotLink USA — including course enrollments, marketplace transactions, and membership upgrades — are final and non-refundable. By completing a purchase, you acknowledge and agree to this no-refund policy.",
  },
  {
    title: "Why We Have a No-Refund Policy",
    content: "Our crash course content, exam question bank, and study materials are made immediately available upon purchase as digital products. Because you receive instant, full access to all course content at the time of purchase, we are unable to 'take back' the product and therefore cannot offer refunds.",
  },
  {
    title: "Exceptions — Technical Failure Only",
    content: "A refund may be considered solely in the event of a verified technical failure that permanently prevented access to purchased content, despite multiple attempts and our support team being unable to resolve the issue within 72 hours. Such requests must be submitted to support@pilotlinkusa.com within 7 days of purchase.",
  },
  {
    title: "Duplicate Charges",
    content: "If you were charged more than once for the same product due to a payment processing error, please contact support@pilotlinkusa.com immediately. Verified duplicate charges will be refunded within 5–10 business days.",
  },
  {
    title: "Chargebacks",
    content: "Initiating a chargeback or payment dispute for a legitimate purchase is a violation of these Terms. We reserve the right to terminate account access and pursue recovery of funds for fraudulent chargebacks. If you have a concern about a charge, please contact us directly at support@pilotlinkusa.com before disputing with your bank.",
  },
  {
    title: "Stripe Payment Processing",
    stripe: true,
    content: "All payments are processed securely through Stripe. Stripe's standard payment policies apply in addition to our own. PilotLink USA does not store your full credit card information — all payment data is handled exclusively by Stripe's PCI-DSS compliant infrastructure.",
  },
  {
    title: "Contact for Billing Questions",
    content: "For any billing question, reach us at: support@pilotlinkusa.com · Response within 24 business hours.",
  },
];

export default function RefundPolicy({ isDarkMode, toggleTheme }) {
  const D       = !!isDarkMode;
  const bg      = D ? "#0d1f3c" : "#f4f7fb";
  const panel   = D ? "#142a4d" : "#ffffff";
  const mid     = D ? "#1a3358" : "#f0f4f8";
  const border  = D ? "rgba(255,255,255,0.10)" : "#e2e8f0";
  const text    = D ? "#f0f4f8" : "#0d1f3c";
  const textSub = D ? "rgba(240,244,248,0.72)" : "#475569";
  const gold    = "#c9971c";
  const shadow  = D ? "0 2px 8px rgba(0,0,0,0.18)" : "0 2px 8px rgba(13,31,60,0.05)";

  const sectionStyle = (sec) => {
    if (sec.red) {
      return {
        background: D ? "rgba(220,38,38,0.10)" : "#fff1f2",
        border: `1.5px solid ${D ? "rgba(248,113,133,0.30)" : "#fecaca"}`,
        borderRadius: 14,
        padding: "clamp(20px,3vw,28px)",
        boxShadow: shadow,
      };
    }
    if (sec.green) {
      return {
        background: D ? "rgba(5,150,105,0.10)" : "#ecfdf5",
        border: `1.5px solid ${D ? "rgba(52,211,153,0.25)" : "#6ee7b7"}`,
        borderRadius: 14,
        padding: "clamp(20px,3vw,28px)",
        boxShadow: shadow,
      };
    }
    if (sec.stripe) {
      return {
        background: D ? "rgba(201,151,28,0.08)" : "#fffbeb",
        border: `1.5px solid rgba(201,151,28,0.25)`,
        borderRadius: 14,
        padding: "clamp(20px,3vw,28px)",
        boxShadow: shadow,
      };
    }
    return {
      background: panel,
      border: `1px solid ${border}`,
      borderRadius: 14,
      padding: "clamp(20px,3vw,28px)",
      boxShadow: shadow,
    };
  };

  const titleColor = (sec) => {
    if (sec.red)    return D ? "#fca5a5" : "#dc2626";
    if (sec.green)  return D ? "#34d399" : "#059669";
    if (sec.stripe) return gold;
    return text;
  };

  return (
    <div style={{ minHeight:"100vh", background:bg, fontFamily:"'Inter',sans-serif", transition:"background .3s" }}>
      <Navbar isDarkMode={D} toggleTheme={toggleTheme}/>
      

      {/* Hero */}
      <div style={{ background:"linear-gradient(135deg,#0d1f3c 0%,#1a3358 100%)", padding:"clamp(44px,6vw,80px) clamp(16px,4vw,32px)", position:"relative", overflow:"hidden" }}>
        <div aria-hidden style={{ position:"absolute", inset:0, backgroundImage:"radial-gradient(rgba(255,255,255,0.05) 1px,transparent 1px)", backgroundSize:"18px 18px", pointerEvents:"none" }}/>
        <div style={{ maxWidth:820, margin:"0 auto", position:"relative", zIndex:1, textAlign:"center" }}>
          <div style={{ fontSize:"clamp(40px,6vw,60px)", marginBottom:16 }}>💳</div>
          <h1 style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:"clamp(28px,5vw,46px)", fontWeight:900, color:"#fff", margin:"0 0 12px", lineHeight:1.1 }}>
            Refund Policy
          </h1>
          <p style={{ fontSize:"clamp(14px,2vw,17px)", color:"rgba(255,255,255,0.60)", marginBottom:16, lineHeight:1.7 }}>
            All purchases on PilotLink USA are final
          </p>
          <div style={{ display:"inline-flex", alignItems:"center", gap:8, background:"rgba(201,151,28,0.15)", border:"1px solid rgba(201,151,28,0.35)", borderRadius:99, padding:"6px 16px", fontSize:12, fontWeight:700, color:gold, letterSpacing:".06em" }}>
            📅 Last updated: July 2, 2026
          </div>
        </div>
      </div>

      {/* Content */}
      <div style={{ maxWidth:820, margin:"0 auto", padding:"clamp(32px,5vw,56px) clamp(16px,4vw,32px)" }}>
        <div style={{ display:"flex", flexDirection:"column", gap:"clamp(14px,2vw,20px)" }}>
          {SECTIONS.map((sec, i) => (
            <div key={i} style={sectionStyle(sec)}>
              <h2 style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:"clamp(16px,2vw,19px)", fontWeight:800, color:titleColor(sec), margin:"0 0 12px", lineHeight:1.3 }}>
                {sec.title}
              </h2>
              <p style={{ fontSize:"clamp(14px,1.8vw,16px)", color:textSub, lineHeight:1.85, margin:0 }}>
                {sec.content}
              </p>
            </div>
          ))}
        </div>

        {/* Footer note */}
        <div style={{ marginTop:"clamp(32px,4vw,48px)", padding:"clamp(16px,2.5vw,24px)", borderRadius:14, background:mid, border:`1px solid ${border}`, textAlign:"center" }}>
          <p style={{ fontSize:13, color:textSub, lineHeight:1.8, margin:0 }}>
            Questions about this policy? Contact us at{" "}
            <a href="mailto:support@pilotlinkusa.com" style={{ color:gold, fontWeight:700, textDecoration:"none" }}>
              support@pilotlinkusa.com
            </a>
          </p>
        </div>
      </div>

      <Footer isDarkMode={D}/>
    </div>
  );
}
